import React, { useContext } from 'react';

interface Config {
  enableSprite?: boolean;
  id?: string;
}

interface IconDefinition {
  icon: string;
  paths: string[];
}

interface ContextValue {
  icons: IconDefinition[];
  config: Config;
}

const IconProviderContext = React.createContext<ContextValue>({
  config: {},
  icons: []
});

function useIconProvider() {
  return useContext(IconProviderContext);
}

function IconSymbol(props: { paths: string[]; icon: string; id?: string }) {
  const paths = props.paths.map((path: string, key: number) => {
    return <path key={key} d={path}></path>;
  });
  return (
    <symbol xmlns="http://www.w3.org/2000/svg" id={`${props.id}${props.icon}`}>
      {paths}
    </symbol>
  );
}

const generateSprite = (icons: IconDefinition[], { id }: Config) => {
  return (
    <svg width="0" height="0" className="hidden" style={{ position: 'absolute' }}>
      {icons.map((icon) => {
        return <IconSymbol paths={icon.paths} icon={icon.icon} id={id} key={icon.icon} />;
      })}
    </svg>
  );
};

const IconProvider = ({
  children,
  iconProvider
}: React.PropsWithChildren<{ iconProvider: ContextValue }>) => {
  const { config, icons } = iconProvider;
  const value = React.useMemo(() => {
    return {
      icons,
      config
    };
  }, [config, icons]);

  if (config && config.enableSprite) {
    return (
      <IconProviderContext.Provider value={value}>
        {generateSprite(icons, config)}
        {children}
      </IconProviderContext.Provider>
    );
  } else {
    return <IconProviderContext.Provider value={value}>{children}</IconProviderContext.Provider>;
  }
};

export { IconProviderContext, IconProvider, useIconProvider };
